<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AddContactAutomationRule from './AddContactAutomationRule.vue';
import EditContactAutomationRule from './EditContactAutomationRule.vue';
import { eventOptions, getOptionLabel } from './contactAutomationOptions';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedRule = ref({});
const loading = ref({});

const accountId = computed(() => getters.getCurrentAccountId.value);
const records = computed(() => getters['contactAutomationRules/getRules'].value);
const uiFlags = computed(
  () => getters['contactAutomationRules/getUIFlags'].value
);

const fetchList = () =>
  store.dispatch('contactAutomationRules/get', { accountId: accountId.value });

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = rule => {
  selectedRule.value = rule;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  selectedRule.value = {};
  showEditPopup.value = false;
};

const openDeletePopup = rule => {
  selectedRule.value = rule;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteMessage = computed(() => ` ${selectedRule.value.name || ''}?`);

const confirmDeletion = async () => {
  const id = selectedRule.value.id;
  closeDeletePopup();
  loading.value[id] = true;
  try {
    await store.dispatch('contactAutomationRules/delete', {
      accountId: accountId.value,
      id,
    });
    useAlert(t('CONTACT_AUTOMATION.TOAST.DELETED'));
  } catch (error) {
    useAlert(t('CONTACT_AUTOMATION.TOAST.DELETE_ERROR'));
  } finally {
    loading.value[id] = false;
  }
};

const toggleRule = async rule => {
  loading.value[rule.id] = true;
  try {
    await store.dispatch('contactAutomationRules/update', {
      accountId: accountId.value,
      id: rule.id,
      rule: { active: !rule.active },
    });
    useAlert(t('CONTACT_AUTOMATION.TOAST.STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('CONTACT_AUTOMATION.TOAST.STATUS_UPDATE_ERROR'));
  } finally {
    loading.value[rule.id] = false;
  }
};

const formatDate = value => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const formatEvent = value => getOptionLabel(eventOptions, value);

onBeforeMount(() => {
  fetchList();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('CONTACT_AUTOMATION.LOADING_MESSAGE')"
    :no-records-found="!records.length"
    :no-records-message="t('CONTACT_AUTOMATION.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('CONTACT_AUTOMATION.HEADER')"
        :description="t('CONTACT_AUTOMATION.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="t('CONTACT_AUTOMATION.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <tr>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">{{ t('CONTACT_AUTOMATION.TABLE.NAME') }}</th>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">{{ t('CONTACT_AUTOMATION.TABLE.EVENT') }}</th>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">{{ t('CONTACT_AUTOMATION.TABLE.ACTIVE') }}</th>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">{{ t('CONTACT_AUTOMATION.TABLE.CREATED_AT') }}</th>
            <th class="py-4 font-semibold text-right text-n-slate-11">{{ t('CONTACT_AUTOMATION.TABLE.ACTIONS') }}</th>
          </tr>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="rule in records" :key="rule.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">{{ rule.name }}</span>
              <div class="text-xs text-n-slate-10">{{ rule.description }}</div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ formatEvent(rule.event_name) }}</td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ rule.active ? t('CONTACT_AUTOMATION.YES') : t('CONTACT_AUTOMATION.NO') }}</td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ formatDate(rule.created_at) }}</td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="t('CONTACT_AUTOMATION.TOOLTIPS.TOGGLE')"
                  icon="i-lucide-power"
                  xs
                  faded
                  :is-loading="loading[rule.id]"
                  @click="toggleRule(rule)"
                />
                <Button
                  v-tooltip.top="t('CONTACT_AUTOMATION.TOOLTIPS.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[rule.id]"
                  @click="openEditPopup(rule)"
                />
                <Button
                  v-tooltip.top="t('CONTACT_AUTOMATION.TOOLTIPS.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[rule.id]"
                  @click="openDeletePopup(rule)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddContactAutomationRule @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditContactAutomationRule :selected-rule="selectedRule" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('CONTACT_AUTOMATION.DELETE_CONFIRM.TITLE')"
      :message="t('CONTACT_AUTOMATION.DELETE_CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="t('CONTACT_AUTOMATION.DELETE_CONFIRM.CONFIRM')"
      :reject-text="t('CONTACT_AUTOMATION.DELETE_CONFIRM.CANCEL')"
    />
  </SettingsLayout>
</template>
